import React, { forwardRef } from 'react';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';

import type { FieldBaseProps, SelectOption } from './field.types';
import { FieldShell, describedBy, labelledBy } from './FieldShell';
import { useFieldIds } from './useFieldIds';

export interface RadioGroupInputProps<T extends string | number> extends FieldBaseProps {
  options: readonly SelectOption<T>[];
  value: T | null | undefined;
  onChange: (value: T, event: React.ChangeEvent<HTMLInputElement>) => void;
  row?: boolean;
  name?: string;
}

function RadioGroupInputInner<T extends string | number>(props: RadioGroupInputProps<T>, ref: React.ForwardedRef<HTMLDivElement>) {
  const { id: idProp, label, hideLabel, helperText, describedById, error, required, hideRequiredMark, disabled, size = 'small', fullWidth = true, className, sx, options, value, onChange, row, name } = props;
  const ids = useFieldIds(idProp);
  return (
    <FieldShell ref={ref} kind="radiogroup" ids={ids} label={label} hideLabel={hideLabel} helperText={helperText} error={error} required={required} hideRequiredMark={hideRequiredMark} disabled={disabled} size={size} fullWidth={fullWidth} className={className} sx={sx}>
      <RadioGroup
        row={row}
        name={name}
        aria-labelledby={labelledBy(ids, label)}
        aria-describedby={describedBy(ids, helperText, describedById)}
        aria-invalid={error || undefined}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(event, raw) => {
          const option = options.find((o) => String(o.value) === raw);
          if (option) onChange(option.value, event);
        }}
      >
        {options.map((o) => (
          <FormControlLabel key={String(o.value)} value={String(o.value)} label={o.label} disabled={o.disabled} control={<Radio size={size} />} />
        ))}
      </RadioGroup>
    </FieldShell>
  );
}

export const RadioGroupInput = forwardRef(RadioGroupInputInner) as <T extends string | number>(
  props: RadioGroupInputProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
